import Table from "components/Table/Table.tsx";
import { SECTION } from "constants/SettingsConstants";
import { commonColumnProps } from "constants/TableConstants";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import { setCurrentSection, setIsLoading } from "reducers/main";
import { setRRE } from "reducers/rre";
import { fetchRreAndUpdateStore } from "reducers/rre.ts";
import { useGetRresQuery } from "services/rre-api";
import type { RREDataType } from "types/DataTypes";
import { useToken } from "utils/context.ts";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM, useUrlStore } from "utils/useUrlStore.ts";

import RREPageContainer from "./RREPageContainer";

const PAGE_SIZE = 25;

const RRE_COLUMNS = [
  { key: "rre_id", name: "RRE ID", ...commonColumnProps },
  {
    key: "client_name",
    name: "Client Name",
    ...commonColumnProps,
    renderCell: ({ row }: { row: RREDataType }) => (
      <div>{row.client_name ?? row.rre_profile?.client_name}</div>
    ),
  },
  { key: "name", name: "Name", ...commonColumnProps },
  { key: "street", name: "Street", ...commonColumnProps },
  { key: "city", name: "City", ...commonColumnProps },
  { key: "state_verbose", name: "State", ...commonColumnProps },
  { key: "zipcode", name: "Zipcode", ...commonColumnProps },
];

const RRETablePage = () => {
  const query = useQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useToken();
  const [page, setPage] = useState(1);
  const { getFilters } = useUrlStore();
  const filters = getFilters();

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.SECTION_111_RRES));
  }, [dispatch]);

  const {
    data: rres = { results: [], count: 0 },
    isLoading,
    isFetching,
  } = useGetRresQuery(
    {
      searchTerm: query.get(GLOBAL_SEARCH_PARAM),
      filters: {
        ...filters,
        limit: PAGE_SIZE.toString(),
        offset: ((page - 1) * PAGE_SIZE).toString(),
      },
    },
    { skip: false },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const handleCellClick = (args: CellClickArgs<RREDataType, unknown>) => {
    dispatch(
      fetchRreAndUpdateStore({ accessToken, rreId: args.row.id.toString() }),
    );
    dispatch(setRRE(args.row));
    navigate(`${location.pathname}/${args.row.id}/overview`);
  };

  return (
    <RREPageContainer>
      <Table
        isSortable={true}
        className="rre-table"
        columns={RRE_COLUMNS}
        rows={rres.results}
        sortColumns={[]}
        onCellClick={handleCellClick}
        rowKeyGetter={(row) => row.id}
        isLoading={isFetching}
        fullHeight
        pagination={{
          pageSize: PAGE_SIZE,
          pageIndex: page,
          rowCount: rres.count,
          onPageChange: (pageIndex) => setPage(pageIndex),
          isLoading: isFetching,
        }}
      />
    </RREPageContainer>
  );
};

export default RRETablePage;
